import React from "react";

import {
  ColorBackground,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  MetricArrayType,
  MetricType,
  VariationDto,
} from "@intelligems/ig-types";
import { InfoOutlined } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useConfiguration } from "../../../../api/configurations/use-configuration";
import {
  currencyFormatter,
  noDecimalCurrencyFormatter,
  noDecimalNumberFormatter,
  noPrefixCurrencyFormatter,
  percentFormatter,
} from "../../../../api/formatters/dashboard";
import { VariationFe } from "../../../../types/schemas/variations";
import { sortByOrder } from "../../../../utils/sortings";
import {
  headerFontSize,
  headerFontWeight,
} from "../../Graphs/Utils/graphStyleCustoms";

interface ProfitTableProps {
  variations: VariationFe[];
  profitMetrics: MetricArrayType | undefined;
}

const ROW_HEIGHT = "27px";
const PADDING_UP_DOWN = "0px";
const StyledTable = styled(TableContainer)(({ theme }) => ({
  [`& .MuiTableBody-root > .MuiTableRow-root`]: {
    height: ROW_HEIGHT,
  },
  [`& .MuiTableHead-root > .MuiTableRow-root`]: {
    height: ROW_HEIGHT,
  },
  [`& .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root`]: {
    height: ROW_HEIGHT,
    paddingTop: PADDING_UP_DOWN,
    paddingBottom: PADDING_UP_DOWN,
  },
  [`& .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root`]: {
    height: ROW_HEIGHT,
    paddingTop: PADDING_UP_DOWN,
    paddingBottom: PADDING_UP_DOWN,
    lineHeight: "unset",
  },
}));

const StyledTableCellHeader = styled(TableCell)(({ theme }) => ({
  [`&.MuiTableCell-root`]: {
    borderBottom: "1px solid transparent",
    paddingBottom: "0px",
  },
}));

const StyledTableCellBody = styled(TableCell)(({ theme }) => ({
  borderBottom: "1px solid transparent",
}));

// styled profit header for profit rows
const StyledTableCellBodyProfit = styled(StyledTableCellHeader)(
  ({ theme }) => ({
    color: theme.palette.primary.dark,
    fontWeight: 600,
  })
);

const StyledTableRowProfit = styled(TableRow)(({ theme }) => ({
  backgroundColor: ColorBackground,
}));

export const ProfitTable = ({
  variations,
  profitMetrics,
}: ProfitTableProps) => {
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const sortedData = (profitMetrics || [])
    .map((group: any) => {
      for (const testGroup of variations) {
        if (group.label === testGroup.id) {
          return { ...group, order: testGroup.order };
        }
      }
      return { ...group, order: 99 };
    })
    .sort(sortByOrder);

  if (!profitMetrics || profitMetrics.length === 0) {
    return (
      <Paper
        variant={"outlined"}
        style={{
          height: "823px",
          textAlign: "center",
        }}
      >
        <div style={{ marginTop: "400px" }}>
          <CircularProgress />
        </div>
      </Paper>
    );
  }

  return (
    <StyledTable
      sx={{
        padding: "30px",
        width: "auto",
      }}
      // @ts-ignore
      component={Paper}
      variant={"outlined"}
    >
      <Grid
        container
        sx={{
          justifyContent: "center",
          marginBottom: "23px",
        }}
        alignItems={"center"}
        item
        direction={"row"}
      >
        <Grid item>
          <IgTypography
            sx={{ fontSize: headerFontSize, fontWeight: headerFontWeight }}
          >
            Profit Analysis
          </IgTypography>
        </Grid>
        <Grid item>
          <IgTooltip
            title={
              <p>
                Summary of revenue, costs and profit, including both product and
                shipping contribution. Metrics are shown in total and normalized
                per order and per visitor at the bottom of the table.
              </p>
            }
          >
            <IconButton size={"small"} aria-label="info">
              <InfoOutlined fontSize={"small"} />
            </IconButton>
          </IgTooltip>
        </Grid>
      </Grid>
      <Table aria-label="profit-table">
        <TableHead>
          <TableRow>
            {sortedData.length < 5 ? (
              <TableCell colSpan={1} />
            ) : (
              <StyledTableCellBody colSpan={1} />
            )}
            {sortedData.map((metric: MetricType) => (
              <TableCell
                sx={{ verticalAlign: "top" }}
                key={metric["label"]}
                align={"right"}
              >
                <Typography variant={"subtitle2"} sx={{ fontWeight: 500 }}>
                  {variations.find((v) => v.id === metric["label"])?.name || ""}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <>
            <TableRow>
              <StyledTableCellHeader sx={{ fontWeight: 700, fontSize: "15px" }}>
                Product
              </StyledTableCellHeader>
              {variations.map((group, index: number) => (
                <StyledTableCellHeader key={`${group.id}-${index}`} />
              ))}
            </TableRow>
            <TableRow>
              <StyledTableCellBody>Revenue</StyledTableCellBody>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBody
                  key={`TOTAL_PRODUCT_REVENUE-${index}`}
                  align={"right"}
                >
                  {noDecimalCurrencyFormatter(
                    metric["TOTAL_PRODUCT_REVENUE"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBody>
              ))}
            </TableRow>
            <TableRow>
              <StyledTableCellBody>COGS</StyledTableCellBody>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBody
                  key={`TOTAL_PRODUCT_COGS-${index}`}
                  align={"right"}
                >
                  (
                  {noPrefixCurrencyFormatter(
                    metric["TOTAL_PRODUCT_COGS"] as number,
                    storeCurrencyConfig!.value!
                  )}
                  )
                </StyledTableCellBody>
              ))}
            </TableRow>
          </>
          <>
            <StyledTableRowProfit>
              <StyledTableCellBodyProfit>
                Product Profit
              </StyledTableCellBodyProfit>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBodyProfit
                  key={`TOTAL_PRODUCT_PROFIT-${index}`}
                  align={"right"}
                >
                  {noDecimalCurrencyFormatter(
                    metric["TOTAL_PRODUCT_PROFIT"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBodyProfit>
              ))}
            </StyledTableRowProfit>
            <StyledTableRowProfit>
              <StyledTableCellBody sx={{ fontStyle: "italic" }}>
                % Margin
              </StyledTableCellBody>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBody
                  key={`TOTAL_PRODUCT_MARGIN-${index}`}
                  align={"right"}
                  sx={{ fontStyle: "italic" }}
                >
                  {percentFormatter(
                    metric["TOTAL_PRODUCT_MARGIN"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBody>
              ))}
            </StyledTableRowProfit>
          </>
          <TableRow>
            <StyledTableCellBody />
            {variations.map((group, index: number) => (
              <StyledTableCellBody key={`${group.id}-${index}`} />
            ))}
          </TableRow>
          <>
            <TableRow>
              <StyledTableCellHeader sx={{ fontWeight: 700, fontSize: "15px" }}>
                Shipping
              </StyledTableCellHeader>
              {variations.map((group, index: number) => (
                <StyledTableCellBody key={`${group.id}-${index}`} />
              ))}
            </TableRow>
            <TableRow>
              <StyledTableCellBody>Revenue</StyledTableCellBody>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBody
                  align={"right"}
                  key={`TOTAL_SHIPPING_REVENUE-${index}`}
                >
                  {noDecimalCurrencyFormatter(
                    metric["TOTAL_SHIPPING_REVENUE"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBody>
              ))}
            </TableRow>
            <TableRow>
              <StyledTableCellBody>Cost</StyledTableCellBody>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBody
                  align={"right"}
                  key={`TOTAL_SHIPPING_COST-${index}`}
                >
                  (
                  {noPrefixCurrencyFormatter(
                    metric["TOTAL_SHIPPING_COST"] as number,
                    storeCurrencyConfig!.value!
                  )}
                  )
                </StyledTableCellBody>
              ))}
            </TableRow>
          </>
          <>
            <StyledTableRowProfit>
              <StyledTableCellBodyProfit>
                Shipping Profit
              </StyledTableCellBodyProfit>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBodyProfit
                  align={"right"}
                  key={`TOTAL_SHIPPING_PROFIT-${index}`}
                >
                  {noDecimalCurrencyFormatter(
                    metric["TOTAL_SHIPPING_PROFIT"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBodyProfit>
              ))}
            </StyledTableRowProfit>
          </>
          <TableRow>
            <StyledTableCellBody />
            {variations.map((group, index: number) => (
              <StyledTableCellBody key={`${group.id}-${index}`} />
            ))}
          </TableRow>
          <TableRow>
            <StyledTableCellHeader sx={{ fontWeight: 700, fontSize: "15px" }}>
              Other
            </StyledTableCellHeader>
            {variations.map((group, index: number) => (
              <StyledTableCellBody key={`${group.id}-${index}`} />
            ))}
          </TableRow>
          <TableRow>
            <StyledTableCellBody>Transaction Fees</StyledTableCellBody>
            {sortedData.map((metric: MetricType, index: number) => (
              <StyledTableCellBody
                align={"right"}
                key={`TRANSACTION_FEE-${index}`}
              >
                (
                {noDecimalCurrencyFormatter(
                  metric["TRANSACTION_FEE"] as number,
                  false,
                  storeCurrencyConfig!.value!
                )}
                )
              </StyledTableCellBody>
            ))}
          </TableRow>
          <TableRow>
            <StyledTableCellBody />
            {variations.map((group, index: number) => (
              <StyledTableCellBody key={`${group.id}-${index}`} />
            ))}
          </TableRow>
          <>
            <TableRow>
              <StyledTableCellHeader sx={{ fontWeight: 700, fontSize: "15px" }}>
                Total
              </StyledTableCellHeader>
              {variations.map((group, index: number) => (
                <StyledTableCellBody key={`${group.id}-${index}`} />
              ))}
            </TableRow>
            <TableRow>
              <StyledTableCellBody>Revenue</StyledTableCellBody>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBody
                  align={"right"}
                  key={`TOTAL_REVENUE-${index}`}
                >
                  {noDecimalCurrencyFormatter(
                    metric["TOTAL_REVENUE"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBody>
              ))}
            </TableRow>
            <TableRow>
              <StyledTableCellBody>Cost</StyledTableCellBody>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBody
                  align={"right"}
                  key={`TOTAL_COST-${index}`}
                >
                  (
                  {noPrefixCurrencyFormatter(
                    metric["TOTAL_COST"] as number,
                    storeCurrencyConfig!.value!
                  )}
                  )
                </StyledTableCellBody>
              ))}
            </TableRow>
          </>
          <>
            <StyledTableRowProfit>
              <StyledTableCellBodyProfit>
                Total Profit
              </StyledTableCellBodyProfit>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBodyProfit
                  align={"right"}
                  key={`TOTAL_PROFIT-${index}`}
                >
                  {noDecimalCurrencyFormatter(
                    metric["TOTAL_PROFIT"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBodyProfit>
              ))}
            </StyledTableRowProfit>
            <StyledTableRowProfit>
              <StyledTableCellBody sx={{ fontStyle: "italic" }}>
                % Margin
              </StyledTableCellBody>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBody
                  sx={{ fontStyle: "italic" }}
                  align={"right"}
                  key={`TOTAL_MARGIN-${index}`}
                >
                  {percentFormatter(
                    metric["TOTAL_MARGIN"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBody>
              ))}
            </StyledTableRowProfit>
          </>
          <TableRow>
            <StyledTableCellBody />
            {variations.map((group, index: number) => (
              <StyledTableCellBody key={`${group.id}-${index}`} />
            ))}
          </TableRow>
          <>
            <TableRow>
              <StyledTableCellHeader>Visitors</StyledTableCellHeader>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBody
                  align={"right"}
                  key={`TOTAL_VISITORS-${index}`}
                >
                  {noDecimalNumberFormatter(
                    metric["VISITORS"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBody>
              ))}
            </TableRow>
            <TableRow>
              <StyledTableCellBodyProfit>
                Profit per Visitor
              </StyledTableCellBodyProfit>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBodyProfit
                  align={"right"}
                  key={`PROFIT_PER_VISITOR-${index}`}
                >
                  {currencyFormatter(
                    metric["PROFIT_PER_VISITOR"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBodyProfit>
              ))}
            </TableRow>
          </>
          <TableRow>
            <StyledTableCellBody />
            {variations.map((group, index: number) => (
              <StyledTableCellBody key={`${group.id}-${index}`} />
            ))}
          </TableRow>
          <>
            <TableRow>
              <StyledTableCellHeader>Orders</StyledTableCellHeader>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBody align={"right"} key={`ORDERS-${index}`}>
                  {noDecimalNumberFormatter(
                    metric["ORDERS"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBody>
              ))}
            </TableRow>
            <TableRow>
              <StyledTableCellBodyProfit>
                Profit per Order
              </StyledTableCellBodyProfit>
              {sortedData.map((metric: MetricType, index: number) => (
                <StyledTableCellBodyProfit
                  align={"right"}
                  key={`PROFIT_PER_ORDER-${index}`}
                >
                  {currencyFormatter(
                    metric["PROFIT_PER_ORDER"] as number,
                    false,
                    storeCurrencyConfig!.value!
                  )}
                </StyledTableCellBodyProfit>
              ))}
            </TableRow>
          </>
        </TableBody>
      </Table>
    </StyledTable>
  );
};
